import { TextoCortado } from '../../componentes/texto-cortado';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, FileText, Mail, MoreHorizontal, Paperclip, Plus, RotateCcw, Trash2, Upload, Users, X } from 'lucide-react';
import { Botao, Selo, type CorSelo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { uuidDaUrl } from '../../componentes/filtros-lista';
import { Selecao } from '../../componentes/formulario';
import { Menu } from '../../componentes/menu';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, del, get, getTodos, mensagemDeErro, post } from '../../nucleo/api';
import { data, dataHora, email, nomeDeArquivo, nomePessoa, textoLivre } from '../../nucleo/formato';
import { hojeNoFuso } from '../../componentes/periodo/periodo';
import { useSessao } from '../../nucleo/sessao';
import { BotaoAtalhoTarefas } from './AtalhoTarefas';
import { DialogoConcluir } from './ConcluirAtividades';
import { DialogoJustificativa } from './DialogoJustificativa';
import { TextoRico } from '../../componentes/editor-rico';
import { EmailNegociacao, tamanhoArquivo } from './FichaPropostas';
import { AtaComArquivos, ListaArquivosNegociacao } from './email-anexos';
import { useOutrosServicos } from './PortfoliosCentral';
import { FormTarefa, rascunhoDaAtividade, rascunhoInicial } from './FormTarefa';
import { ProximaTarefa, useWorkflows } from './ProximaTarefa';
import { ResponderQuestionario, type AplicacaoQuestionario, type QuestionarioDaNegociacao } from './Questionarios';
import { caminhoDoAgendamento } from './crm-caminhos';
import { ESTADOS_REUNIAO_ABERTA, reunioesSemAtividade, tituloDaReuniao, useReunioesDaNegociacao, useVeReunioes, type ReuniaoAgenda } from './reunioes-agenda';
import { IconeTipoAtividade } from './tarefas-comum';
import { LinhaTarefaExcluida, TarefasExcluidas, useExcluidasDaNegociacao, useRestaurarTarefaExcluida, type TarefaExcluida } from './TarefasExcluidas';
import type { Atividade, Oportunidade } from './tipos';

/** CRM no novo desenho, lote 4: abas Tarefas, E-mail, Questionários e Arquivos da ficha da negociação (o RD). */

const ENTIDADE = 'pipeline.oportunidade';
const COR: Record<string, CorSelo> = { atrasada: 'laranja', futura: 'azul', concluida: 'verde', cancelada: 'neutro' };
const COR_REUNIAO: Record<ReuniaoAgenda['estado'], CorSelo> = { marcado: 'azul', confirmado: 'azul', realizado: 'verde', faltou: 'laranja', cancelado: 'neutro' };
const statusDa = (a: Atividade) => (a.estado === 'concluida' ? 'concluida' : a.estado === 'cancelada' ? 'cancelada' : a.atrasada ? 'atrasada' : 'futura');

export const useAtividadesDaNegociacao = (id: string) =>
  useQuery({ queryKey: ['pipeline', 'atividades-da-oportunidade', id], queryFn: () => getTodos<Atividade>('/pipeline/atividades', { oportunidade_id: id, filtro: 'todas' }) });

// rlb-5 VIS-M1: no celular, marcador, tipo e título ficam juntos numa linha inteira e o resto (selo, data, ações) quebra para
// baixo, em vez de espremer o título; a partir de 640 px o invólucro some (`contents`) e a linha é a de antes.
const CABECA_LINHA = 'flex w-full min-w-0 items-center gap-3 sm:contents';

/**
 * Lista B de 30/10 (item 29): reunião da Agenda ligada à negociação, na mesma lista das tarefas, com o tipo (Reunião) visível.
 * Abre no agendamento da Agenda; a edição é de lá (a que nasceu de tarefa é a própria atividade e não passa por aqui).
 */
function LinhaReuniaoAgenda({ r, op, compacto, destacada }: { r: ReuniaoAgenda; op: Oportunidade; compacto?: boolean; destacada: boolean }) {
  const { t } = useTranslation('pipeline');
  const s = useSessao();
  const titulo = textoLivre(tituloDaReuniao(r) || t('crm.ficha.reuniaoAgenda.semAssunto'), op.parceiro_nome);
  return (
    <li className={`flex flex-wrap items-center gap-3 px-3 py-2.5 text-sm ${destacada ? 'ring-2 ring-inset ring-acento-forte' : ''}`} data-reuniao-agenda={r.id} data-destaque-tarefa={destacada ? r.id : undefined} aria-current={destacada ? 'true' : undefined}>
      <div className={CABECA_LINHA} data-cabeca-linha="">
        <Users className={`size-5 shrink-0 ${r.estado === 'realizado' ? 'text-verde' : 'text-tinta-3'}`} aria-hidden />
        <span className="inline-flex items-center gap-1 text-xs text-tinta-3" title={t('tipoAtividade.reuniao')}>
          <span className={compacto ? 'sr-only' : ''}>{t('tipoAtividade.reuniao')}</span>
        </span>
        <Link to={caminhoDoAgendamento(r.id)} className="min-w-0 flex-1 truncate text-left font-medium hover:underline" title={`${t('crm.ficha.reuniaoAgenda.abrir')}: ${titulo}`} data-abrir-reuniao-agenda="">
          {titulo}
        </Link>
      </div>
      <Selo cor={COR_REUNIAO[r.estado]}>{t(`crm.ficha.reuniaoAgenda.estado.${r.estado}`)}</Selo>
      <span className="numero text-xs text-tinta-2">{r.dia_inteiro ? `${data(hojeNoFuso(s.workspace.fuso, new Date(r.inicio)))}, ${t('crm.ficha.reuniaoAgenda.diaInteiro')}` : dataHora(r.inicio)}</span>
    </li>
  );
}

/**
 * Tarefas da negociação, com concluir pela ata e "Criar próxima tarefa" (lote 2) e criar ou editar. Também usada no bloco
 * "Próximas tarefas" do topo (`so` pendentes e `limite`).
 */
export function TarefasDaNegociacao({ op, podeEditar, compacto }: { op: Oportunidade; podeEditar: boolean; compacto?: boolean }) {
  const { t } = useTranslation('pipeline');
  const s = useSessao();
  const q = useAtividadesDaNegociacao(op.id);
  const workflows = useWorkflows();
  // Lista de 30/10 (B), item 36: "Excluídas" mostra as tarefas da negociação que estão na Lixeira (e restaura ali mesmo).
  const [filtro, setFiltro] = useState<'pendentes' | 'concluidas' | 'todas' | 'excluidas'>('pendentes');
  const [form, setForm] = useState<{ atual?: Atividade } | null>(null);
  const [concluir, setConcluir] = useState<Atividade | null>(null);
  const [proxima, setProxima] = useState<{ a: Atividade; ata: string } | null>(null);
  const [ata, setAta] = useState<Atividade | null>(null);
  const [excluir, setExcluir] = useState<Atividade | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  // Ajustes do CRM 27/10 (F5, decisão 12): excluir pela linha, sem abrir a tarefa (pendente, concluída ou cancelada).
  const excluirMut = useMutation({
    mutationFn: (v: { a: Atividade; justificativa: string }) => del(`/pipeline/atividades/${v.a.id}`, { justificativa: v.justificativa }),
    onSuccess: async () => {
      setExcluir(null);
      await qc.invalidateQueries({ queryKey: ['pipeline'] });
      await qc.invalidateQueries({ queryKey: ['/pipeline/atividades'] });
      await qc.invalidateQueries({ queryKey: ['chatter', ENTIDADE, op.id] });
      toast.sucesso(t('crm.form.excluida'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  // crm6 item 12: "Cancelar reunião" no "..." da reunião pendente, como em Agenda > Reuniões (o servidor cancela também o
  // agendamento na Agenda, quando a reunião virou um).
  const [cancelarReuniao, setCancelarReuniao] = useState<Atividade | null>(null);
  const cancelarMut = useMutation({
    mutationFn: (v: { a: Atividade; justificativa: string }) => post(`/pipeline/atividades/${v.a.id}/cancelar`, { justificativa: v.justificativa }),
    onSuccess: async () => {
      setCancelarReuniao(null);
      await qc.invalidateQueries({ queryKey: ['pipeline'] });
      await qc.invalidateQueries({ queryKey: ['/pipeline/atividades'] });
      await qc.invalidateQueries({ queryKey: ['chatter', ENTIDADE, op.id] });
      toast.sucesso(t('crm.ficha.reuniaoCancelada'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const podeExcluir = s.pode('pipeline.excluir');
  const aberta = op.estado === 'aberta';
  const todas = (q.data?.itens ?? []).slice().sort((a, b) => a.data_hora.localeCompare(b.data_hora));
  const atividades = compacto ? todas.filter((a) => a.estado === 'pendente') : todas.filter((a) => filtro === 'todas' || (filtro === 'pendentes' ? a.estado === 'pendente' : a.estado === 'concluida'));
  // Lista B de 30/10 (item 29): as reuniões da Agenda ligadas à negociação entram na mesma lista, sem repetir a que já é atividade.
  const veReunioes = useVeReunioes();
  const rq = useReunioesDaNegociacao(op.id);
  const reunioesTodas = veReunioes ? reunioesSemAtividade(rq.data?.itens ?? [], todas) : [];
  const reunioes = reunioesTodas.filter((r) => (compacto || filtro === 'pendentes' ? ESTADOS_REUNIAO_ABERTA.includes(r.estado) : filtro === 'concluidas' ? r.estado === 'realizado' : true));
  // Lista de 02/11 (item 7): "Todas" inclui também as excluídas (as da Lixeira, com selo, quem excluiu, justificativa e Restaurar).
  const comExcluidas = !compacto && filtro === 'todas';
  const eq = useExcluidasDaNegociacao(op.id, comExcluidas);
  const restaurarExcluida = useRestaurarTarefaExcluida(op.id);
  const excluidas = comExcluidas ? (eq.data?.itens ?? []) : [];
  const linhas: ({ tipo: 'atividade'; a: Atividade; quando: string } | { tipo: 'reuniao'; r: ReuniaoAgenda; quando: string } | { tipo: 'excluida'; i: TarefaExcluida; quando: string })[] = [
    ...atividades.map((a) => ({ tipo: 'atividade' as const, a, quando: a.data_hora })),
    ...reunioes.map((r) => ({ tipo: 'reuniao' as const, r, quando: r.inicio })),
    ...excluidas.map((i) => ({ tipo: 'excluida' as const, i, quando: i.quando ?? i.excluido_em })),
  ]
    .sort((x, y) => x.quando.localeCompare(y.quando))
    .slice(0, compacto ? 3 : undefined);
  // Lista B de 30/10 (item 28): `?tarefa=` (ou `?reuniao=`) vindo do atalho de tarefas e reuniões abre a edição daquela tarefa
  // (reunião que é atividade vale como ela); quem não edita, ou a reunião só da Agenda, vê o item em destaque na lista.
  const [busca, setBusca] = useSearchParams();
  const tarefaPedida = uuidDaUrl(busca.get('tarefa'));
  const reuniaoPedida = uuidDaUrl(busca.get('reuniao'));
  const [destaque, setDestaque] = useState('');
  const pronto = !q.isPending && !(veReunioes && rq.isPending);
  useEffect(() => {
    if (compacto || !pronto || (!tarefaPedida && !reuniaoPedida)) return;
    const atv = tarefaPedida ? todas.find((a) => a.id === tarefaPedida) : todas.find((a) => a.agendamento_id === reuniaoPedida);
    if (atv && atv.estado === 'pendente' && podeEditar) setForm({ atual: atv });
    else {
      const alvo = atv?.id ?? (reuniaoPedida && reunioesTodas.some((r) => r.id === reuniaoPedida) ? reuniaoPedida : '');
      if (alvo) {
        setFiltro('todas');
        setDestaque(alvo);
      }
    }
    setBusca(
      (b) => {
        b.delete('tarefa');
        b.delete('reuniao');
        return b;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compacto, pronto, tarefaPedida, reuniaoPedida]);
  useEffect(() => {
    if (destaque) (document.querySelector(`[data-destaque-tarefa="${destaque}"]`) as HTMLElement | null)?.scrollIntoView?.({ block: 'center' });
  }, [destaque, filtro, linhas.length]);
  return (
    <section className="flex flex-col gap-3" data-tarefas-negociacao={compacto ? 'proximas' : 'aba'}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {compacto ? (
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            {t('crm.ficha.proximasTarefas')}
            {/* K3: o mesmo atalho de tarefas da origem, ao lado de "Próximas tarefas". */}
            <BotaoAtalhoTarefas />
          </h2>
        ) : (
          <div className="w-full sm:w-56">
            <Selecao aria-label={t('crm.ficha.filtroTarefas')} value={filtro} opcoes={(['pendentes', 'concluidas', 'todas', 'excluidas'] as const).map((x) => ({ valor: x, rotulo: x === 'excluidas' ? t('crm.excluidas.filtro') : t(`crm.ficha.tarefas.${x}`) }))} onChange={(e) => setFiltro(e.target.value as typeof filtro)} />
          </div>
        )}
        {aberta && podeEditar && s.pode('pipeline.criar') && (
          <Botao tamanho="sm" icone={<Plus className="size-4" />} onClick={() => setForm({})} data-criar-tarefa-negociacao="">
            {t('crm.atividades.criar')}
          </Botao>
        )}
      </div>
      {!compacto && filtro === 'excluidas' ? (
        <TarefasExcluidas op={op} />
      ) : linhas.length === 0 ? (
        comExcluidas && eq.isPending ? null : (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-borda px-4 py-6 text-center text-sm text-tinta-3" data-tarefas-vazio="">
          <p>{compacto ? t('crm.ficha.semPendentes') : t('crm.ficha.tarefas.vazio')}</p>
          {compacto && aberta && podeEditar && s.pode('pipeline.criar') && (
            <Botao tamanho="sm" variante="primario" icone={<Plus className="size-4" />} onClick={() => setForm({})}>
              {t('crm.atividades.criar')}
            </Botao>
          )}
        </div>
        )
      ) : (
        <ul className="divide-y divide-borda rounded-xl border border-borda bg-sup-1">
          {linhas.map((l) => {
            if (l.tipo === 'excluida') return <LinhaTarefaExcluida key={`ex-${l.i.id}`} i={l.i} parceiro={op.parceiro_nome} restaurar={restaurarExcluida} />;
            if (l.tipo === 'reuniao') return <LinhaReuniaoAgenda key={`ag-${l.r.id}`} r={l.r} op={op} compacto={compacto} destacada={destaque === l.r.id} />;
            const a = l.a;
            return (
            <li key={a.id} className={`flex flex-wrap items-center gap-3 px-3 py-2.5 text-sm ${destaque === a.id ? 'ring-2 ring-inset ring-acento-forte' : ''}`} data-tarefa={a.id} data-destaque-tarefa={destaque === a.id ? a.id : undefined} aria-current={destaque === a.id ? 'true' : undefined}>
              <div className={CABECA_LINHA} data-cabeca-linha="">
                {a.estado === 'pendente' && podeEditar ? (
                  <button type="button" aria-label={t('crm.ficha.concluirTarefa', { titulo: textoLivre(a.titulo, op.parceiro_nome) })} title={t('crm.ficha.concluirTarefa', { titulo: textoLivre(a.titulo, op.parceiro_nome) })} onClick={() => setConcluir(a)} className="flex size-6 shrink-0 items-center justify-center rounded border border-borda-campo hover:border-acento-forte" data-concluir-tarefa-ficha={a.id}>
                    <Check className="size-4 text-transparent hover:text-acento-forte" aria-hidden />
                  </button>
                ) : (
                  <Check className={`size-5 shrink-0 ${a.estado === 'concluida' ? 'text-verde' : 'text-tinta-3'}`} aria-hidden />
                )}
                <span className="inline-flex items-center gap-1 text-xs text-tinta-3" title={t(`tipoAtividade.${a.tipo}`)}>
                  <IconeTipoAtividade tipo={a.tipo} />
                  <span className={compacto ? 'sr-only' : ''}>{t(`tipoAtividade.${a.tipo}`)}</span>
                </span>
                <button type="button" className="min-w-0 flex-1 text-left font-medium hover:underline" onClick={() => (a.estado === 'pendente' && podeEditar ? setForm({ atual: a }) : a.ata ? setAta(a) : undefined)}>
                  {textoLivre(a.titulo, op.parceiro_nome)}
                </button>
              </div>
              {a.ata && (
                <button type="button" className="flex items-center gap-1 text-xs text-tinta-3 hover:underline" onClick={() => setAta(a)} data-ver-ata-ficha="">
                  <FileText className="size-3.5" aria-hidden />
                  {t('crm.ficha.ata', { count: a.ata_anexo_ids?.length ?? 0 })}
                </button>
              )}
              <Selo cor={COR[statusDa(a)]}>{t(`crm.atividades.status.${statusDa(a) === 'futura' ? 'futura' : statusDa(a)}`)}</Selo>
              <span className="numero text-xs text-tinta-2">{dataHora(a.data_hora)}</span>
              {!compacto && (a.responsaveis?.length ?? 0) > 0 && (
                <TextoCortado className="max-w-48 text-xs text-tinta-2" data-responsaveis-tarefa="">
                  {a.responsaveis!.map((r) => nomePessoa(r.nome)).join(', ')}
                </TextoCortado>
              )}
              {a.tipo === 'reuniao' && a.estado === 'pendente' && podeEditar && (!a.agendamento_id || s.pode('agenda.cancelar')) && (
                <span data-menu-reuniao={a.id}>
                  <Menu
                    rotulo={t('crm.ficha.maisOpcoesTarefa', { titulo: textoLivre(a.titulo, op.parceiro_nome) })}
                    gatilho={<MoreHorizontal className="size-4" aria-hidden />}
                    classeGatilho="grid size-8 place-items-center rounded-lg text-tinta-2 hover:bg-sup-3 hover:text-tinta-1"
                    classePainel="right-0 w-56"
                    itens={[{ chave: 'cancelar', rotulo: t('crm.ficha.cancelarReuniao'), icone: <X className="size-4" aria-hidden />, perigo: true, aoClicar: () => setCancelarReuniao(a) }]}
                  />
                </span>
              )}
              {!compacto && a.estado === 'cancelada' && podeEditar && aberta && (
                <Botao variante="fantasma" tamanho="sm" icone={<RotateCcw className="size-4" />} aria-label={t('crm.ficha.reabrirTarefa', { titulo: textoLivre(a.titulo, op.parceiro_nome) })} title={t('crm.form.reabrir')} onClick={() => setForm({ atual: a })} data-reabrir-tarefa-ficha={a.id}>
                  {t('crm.form.reabrir')}
                </Botao>
              )}
              {!compacto && podeExcluir && (!a.agendamento_id || s.pode('agenda.cancelar')) && (
                <Botao variante="fantasma" tamanho="sm" icone={<Trash2 className="size-4" />} aria-label={t('crm.ficha.excluirTarefa', { titulo: textoLivre(a.titulo, op.parceiro_nome) })} title={t('crm.form.excluir')} onClick={() => setExcluir(a)} data-excluir-tarefa-ficha={a.id} />
              )}
            </li>
            );
          })}
        </ul>
      )}
      {comExcluidas && eq.isPending && (
        <p role="status" className="text-xs text-tinta-3" data-excluidas-carregando="">
          {t('crm.excluidas.carregandoTodas')}
        </p>
      )}
      {comExcluidas && eq.isError && (
        <p role="alert" className="text-xs text-perigo" data-excluidas-erro="">
          {t('crm.excluidas.erroTodas', { erro: mensagemDeErro(eq.error) })}
        </p>
      )}
      {comExcluidas && eq.data?.truncado && <p className="text-xs text-tinta-3">{t('crm.excluidas.truncado', { n: eq.data.itens.length })}</p>}
      {form && (
        <FormTarefa
          atual={form.atual}
          travarNegociacao
          inicial={form.atual ? rascunhoDaAtividade(form.atual) : rascunhoInicial(op.responsavel_id ?? s.usuario.id, { parceiroId: op.parceiro_id, parceiroNome: op.parceiro_nome, oportunidadeId: op.id, oportunidadeTitulo: op.titulo })}
          aoFechar={() => setForm(null)}
          aoPedirConcluir={(a) => setConcluir(a)}
        />
      )}
      {concluir && (
        <DialogoConcluir
          atividades={[concluir]}
          aoFechar={() => setConcluir(null)}
          aoConcluir={({ ata: texto }) => {
            if (workflows.data?.abrir_proxima ?? true) setProxima({ a: concluir, ata: texto });
          }}
        />
      )}
      {proxima && <ProximaTarefa concluida={proxima.a} ata={proxima.ata} aoFechar={() => setProxima(null)} />}
      <DialogoJustificativa
        aberto={!!cancelarReuniao}
        aoFechar={() => setCancelarReuniao(null)}
        aoConfirmar={(justificativa) => cancelarReuniao && cancelarMut.mutate({ a: cancelarReuniao, justificativa })}
        carregando={cancelarMut.isPending}
        titulo={t('crm.ficha.confirmarCancelarReuniao')}
        texto={t('crm.ficha.confirmarCancelarReuniaoTexto', { titulo: cancelarReuniao ? textoLivre(cancelarReuniao.titulo, op.parceiro_nome) : '' })}
        botao={t('crm.ficha.cancelarReuniao')}
        nomesNoTexto={[op.parceiro_nome]}
      />
      <DialogoJustificativa
        aberto={!!excluir}
        aoFechar={() => setExcluir(null)}
        aoConfirmar={(justificativa) => excluir && excluirMut.mutate({ a: excluir, justificativa })}
        carregando={excluirMut.isPending}
        titulo={t('crm.form.confirmarExcluir')}
        texto={t('crm.form.confirmarExcluirTexto')}
        botao={t('crm.form.excluir')}
        nomesNoTexto={[op.parceiro_nome]}
      />
      {ata && (
        <Modal aberto aoFechar={() => setAta(null)} titulo={t('crm.atividades.ata')}>
          <div className="flex flex-col gap-2 text-sm" data-ver-ata="">
            <AtaComArquivos oportunidadeId={op.id} atividade={ata} parceiro={op.parceiro_nome} />
          </div>
        </Modal>
      )}
    </section>
  );
}

interface EmailEnviado {
  id: string;
  /** Nulo sem leitura do Cadastros (rodada crm2). */
  para: string | null;
  cc?: string | null;
  assunto: string;
  texto: string;
  /** Lote crm4 (L8): corpo formatado (limpo no servidor; o `TextoRico` limpa de novo); null = e-mail só de texto. */
  html?: string | null;
  anexos?: { id: string; nome: string; tamanho: number }[] | null;
  proposta: number | null;
  autor_nome: string | null;
  created_at: string;
}

export function AbaEmail({ op, podeEditar }: { op: Oportunidade; podeEditar: boolean }) {
  const { t } = useTranslation('pipeline');
  // Val5 (29/10): enviar e-mail ao cliente pede `pipeline.enviar`.
  const podeEnviar = useSessao().pode('pipeline.enviar');
  const q = useQuery({ queryKey: ['pipeline', 'emails', op.id], queryFn: () => get<{ itens: EmailEnviado[]; destinatarios: string[] }>(`/pipeline/oportunidades/${op.id}/emails`) });
  const [novo, setNovo] = useState(false);
  // Lista de 03/11, "Regra final": o e-mail da aba abre com o texto do site, como o da proposta.
  const outros = useOutrosServicos();
  return (
    <section className="flex flex-col gap-3" data-aba-email="">
      {podeEditar && podeEnviar && op.estado === 'aberta' && (
        <div className="flex justify-end">
          <Botao icone={<Mail className="size-4" />} onClick={() => setNovo(true)} data-criar-email="">
            {t('crm.email.criar')}
          </Botao>
        </div>
      )}
      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <p className="rounded-xl border border-dashed border-borda px-4 py-8 text-center text-sm text-tinta-3">{t('crm.email.vazio')}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {d.itens.map((e) => (
                <li key={e.id} className="rounded-xl border border-borda bg-sup-1 p-4 text-sm" data-email={e.id}>
                  <p className="text-xs text-tinta-3">
                    {dataHora(e.created_at)}
                    {e.para ? ` · ${t('crm.email.paraQuem', { para: email(e.para) })}` : ''}
                    {e.cc ? ` · ${t('crm.ficha2.email.ccQuem', { lista: e.cc.split(', ').map((x) => email(x)).join(', ') })}` : ''}
                    {e.autor_nome ? ` · ${nomePessoa(e.autor_nome)}` : ''}
                  </p>
                  <h3 className="mt-1 font-semibold">{textoLivre(e.assunto, op.parceiro_nome)}</h3>
                  {e.html ? (
                    <TextoRico className="mt-1 text-tinta-2" html={e.html} texto={(x) => textoLivre(x, op.parceiro_nome)} data-email-html="" />
                  ) : (
                    <p className="mt-1 whitespace-pre-wrap text-tinta-2">{textoLivre(e.texto, op.parceiro_nome)}</p>
                  )}
                  {!!e.anexos?.length && (
                    <ul className="mt-2 flex flex-wrap gap-2" aria-label={t('crm.email.anexos')} data-email-anexos-enviados="">
                      {e.anexos.map((a) => (
                        <li key={a.id}>
                          <a href={`/api/v1/anexos/${a.id}`} className="inline-flex items-center gap-1 rounded-full border border-borda bg-sup-2 px-2.5 py-1 text-xs hover:bg-sup-3" title={t('crm.arquivos.baixar', { nome: nomeDeArquivo(a.nome, op.parceiro_nome) })}>
                            <Paperclip className="size-3.5" aria-hidden />
                            {nomeDeArquivo(a.nome, op.parceiro_nome)}
                            <span className="numero text-tinta-3">{tamanhoArquivo(a.tamanho)}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                  {e.proposta && (
                    <p className="mt-2 flex items-center gap-1 text-xs">
                      <Paperclip className="size-3.5" aria-hidden />
                      {t('crm.proposta.numero', { numero: e.proposta })}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )
        }
      </Estado>
      {novo && outros.isFetched && <EmailNegociacao op={op} inicial={{ assunto: '', texto: outros.data?.texto ?? '' }} semOutros={!outros.data} aoFechar={() => setNovo(false)} />}
    </section>
  );
}

/**
 * Lista de 30/10 (item 14): vários questionários ao mesmo tempo na mesma negociação, do mesmo modelo ou de modelos diferentes.
 * Cada um é uma aplicação com situação, rascunho e respostas próprios. Os formulários já abertos ficam montados (só se
 * escondem ao recolher), então abrir ou recolher um não perde o que foi digitado em outro.
 */
export function AbaQuestionarios({ op, podeEditar }: { op: Oportunidade; podeEditar: boolean }) {
  const { t } = useTranslation('pipeline');
  const qc = useQueryClient();
  const toast = useToast();
  const chave = ['pipeline', 'questionarios-da-negociacao', op.id];
  const q = useQuery({ queryKey: chave, queryFn: () => get<{ itens: QuestionarioDaNegociacao[]; aplicacoes?: AplicacaoQuestionario[] }>(`/pipeline/oportunidades/${op.id}/questionarios`) });
  const [modelo, setModelo] = useState('');
  // `abertos`: os que estão à vista; `vistos`: os que já foram abertos (continuam montados, escondidos, para guardar o digitado).
  const [abertos, setAbertos] = useState<Set<string> | null>(null);
  const [vistos, setVistos] = useState<Set<string>>(new Set());
  const [excluir, setExcluir] = useState<string | null>(null);
  // Os abertos de início ficam fixos na primeira leitura: concluir um não fecha nem abre outro sozinho.
  const inicial = useRef<Set<string> | null>(null);
  const aberta = op.estado === 'aberta';
  const mostrar = (id: string) => {
    setAbertos((x) => new Set([...(x ?? []), id]));
    setVistos((x) => new Set([...x, id]));
  };
  const iniciar = useMutation({
    mutationFn: (questionario_id: string) => post<{ aplicacao_id: string }>(`/pipeline/oportunidades/${op.id}/questionarios`, { questionario_id }),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: chave });
      await qc.invalidateQueries({ queryKey: ['pipeline', 'resumo-negociacao', op.id] });
      mostrar(r.aplicacao_id);
      toast.sucesso(t('crm.questionarios.iniciado'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const apagar = useMutation({
    mutationFn: (id: string) => del(`/pipeline/oportunidades/${op.id}/questionarios/aplicacoes/${id}`),
    onSuccess: async () => {
      setExcluir(null);
      await qc.invalidateQueries({ queryKey: chave });
      await qc.invalidateQueries({ queryKey: ['pipeline', 'resumo-negociacao', op.id] });
      await qc.invalidateQueries({ queryKey: ['pipeline', 'questionario-respostas'] });
      toast.sucesso(t('crm.questionarios.excluido'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <section className="flex min-w-0 flex-col gap-3" data-aba-questionarios="">
      <Estado consulta={q}>
        {(d) => {
          const aplicacoes = d.aplicacoes ?? [];
          const modelos = d.itens.filter((m) => m.ativo);
          const porId = new Map(d.itens.map((m) => [m.id, m]));
          // Sem escolha ainda: as em andamento (até 3) ou a mais recente ficam abertas.
          const emAndamento = aplicacoes.filter((a) => a.situacao === 'em_andamento');
          inicial.current ??= new Set((emAndamento.length ? emAndamento.slice(-3) : aplicacoes.slice(-1)).map((a) => a.id));
          const iniciais = inicial.current;
          const visiveis = abertos ?? iniciais;
          const montados = new Set([...vistos, ...iniciais, ...visiveis]);
          const alternar = (id: string) => {
            const novo = new Set(visiveis);
            if (novo.has(id)) novo.delete(id);
            else novo.add(id);
            setAbertos(novo);
            setVistos((x) => new Set([...x, id]));
          };
          // RL301-VIS-B4: um só botão primário à vista: o "Concluir" da primeira aplicação aberta em andamento; sem nenhuma aberta, o
          // "Novo questionário". Os demais ficam secundários.
          const emFoco = podeEditar && aberta ? (aplicacoes.find((a) => a.situacao === 'em_andamento' && visiveis.has(a.id))?.id ?? null) : null;
          const escolhido = modelos.find((m) => m.id === modelo)?.id ?? modelos[0]?.id ?? '';
          // Nº da aplicação dentro do mesmo modelo, só para distinguir as repetidas.
          const contagem = new Map<string, number>();
          const total = new Map<string, number>();
          for (const a of aplicacoes) total.set(a.questionario_id, (total.get(a.questionario_id) ?? 0) + 1);
          return (
            <>
              {podeEditar && aberta && (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end" data-novo-questionario="">
                  <div className="min-w-0 sm:w-72">
                    {modelos.length ? (
                      <Selecao aria-label={t('crm.questionarios.escolherModelo')} value={escolhido} opcoes={modelos.map((m) => ({ valor: m.id, rotulo: textoLivre(m.nome) }))} onChange={(e) => setModelo(e.target.value)} />
                    ) : (
                      <p className="text-sm text-tinta-3">{t('crm.questionarios.semModelos')}</p>
                    )}
                  </div>
                  {modelos.length > 0 && (
                    <Botao variante={emFoco ? 'secundario' : 'primario'} icone={<Plus className="size-4" />} carregando={iniciar.isPending} onClick={() => iniciar.mutate(escolhido)} data-iniciar-questionario="">
                      {t('crm.questionarios.novo')}
                    </Botao>
                  )}
                </div>
              )}
              {!aplicacoes.length ? (
                <p className="rounded-xl border border-dashed border-borda px-4 py-8 text-center text-sm text-tinta-3">{modelos.length ? t('crm.questionarios.nenhum') : t('crm.questionarios.vazio')}</p>
              ) : (
                <ul className="flex flex-col gap-3" data-lista-questionarios="">
                  {aplicacoes.map((a) => {
                    const m = porId.get(a.questionario_id);
                    if (!m) return null;
                    const n = (contagem.set(a.questionario_id, (contagem.get(a.questionario_id) ?? 0) + 1), contagem.get(a.questionario_id)!);
                    const nome = (total.get(a.questionario_id) ?? 1) > 1 ? t('crm.questionarios.numero', { nome: textoLivre(m.nome), n }) : textoLivre(m.nome);
                    const concluida = a.situacao === 'concluido';
                    const aberto = visiveis.has(a.id);
                    return (
                      <li key={a.id} className="min-w-0 rounded-xl border border-borda bg-sup-1" data-aplicacao-item={a.id} data-situacao={a.situacao}>
                        <button
                          type="button"
                          className="flex w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-1 rounded-xl px-4 py-3 text-left"
                          aria-expanded={aberto}
                          aria-label={t(aberto ? 'crm.questionarios.recolher' : 'crm.questionarios.abrir', { nome })}
                          onClick={() => alternar(a.id)}
                          data-alternar-aplicacao=""
                        >
                          <span className="min-w-0 flex-1 truncate font-medium" title={nome}>
                            {nome}
                          </span>
                          <Selo cor={concluida ? 'verde' : 'dourado'}>{t(concluida ? 'crm.questionarios.concluido' : 'crm.questionarios.emAndamento')}</Selo>
                          {a.pontuacao !== null && <span className="text-xs font-semibold text-acento-forte">{t('crm.questionarios.pontosResumo', { count: a.pontuacao })}</span>}
                          <span className="text-xs text-tinta-3">{concluida ? t('crm.questionarios.concluidoEm', { quando: dataHora(a.concluido_em ?? a.atualizado_em) }) : t('crm.questionarios.iniciadoEm', { quando: dataHora(a.criado_em) })}</span>
                        </button>
                        {montados.has(a.id) && (
                          <div className="px-3 pb-3" hidden={!aberto}>
                            <ResponderQuestionario opId={op.id} q={m} aplicacao={a} podeEditar={podeEditar && aberta} primario={a.id === emFoco} aoExcluir={() => setExcluir(a.id)} />
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          );
        }}
      </Estado>
      <Confirmar aberto={!!excluir} aoFechar={() => setExcluir(null)} perigoso carregando={apagar.isPending} titulo={t('crm.questionarios.confirmarExcluirAplicacao')} texto={t('crm.questionarios.confirmarExcluirAplicacaoTexto')} aoConfirmar={() => excluir && apagar.mutate(excluir)} />
    </section>
  );
}

interface AnexoNegociacao {
  id: string;
  nome: string;
  mime: string;
  tamanho: number;
  created_at: string;
  /** Ajustes do CRM (27/10, F13): quem enviou. */
  autor_nome?: string | null;
}
export function AbaArquivos({ op, podeEditar, contatos = [] }: { op: Oportunidade; podeEditar: boolean; contatos?: string[] }) {
  const { t } = useTranslation('pipeline');
  // Rodada crm2 (VIS-M2): nome de arquivo pelo modo discreto, com o cliente e as pessoas de contato.
  const arq = (nome: string) => nomeDeArquivo(nome, op.parceiro_nome, ...contatos);
  const qc = useQueryClient();
  const toast = useToast();
  const entrada = useRef<HTMLInputElement>(null);
  const [arrastando, setArrastando] = useState(false);
  const [excluir, setExcluir] = useState<AnexoNegociacao | null>(null);
  const q = useQuery({ queryKey: ['chatter', ENTIDADE, op.id], queryFn: () => get<{ anexos: AnexoNegociacao[] }>(`/chatter/${ENTIDADE}/${op.id}`) });
  const recarregar = () => qc.invalidateQueries({ queryKey: ['chatter', ENTIDADE, op.id] });
  const enviar = useMutation({
    mutationFn: async (arquivos: File[]) => {
      for (const f of arquivos) {
        const fd = new FormData();
        fd.append('entidade', ENTIDADE);
        fd.append('registro_id', op.id);
        fd.append('arquivo', f);
        await api('POST', '/anexos', fd);
      }
    },
    onSuccess: async () => {
      toast.sucesso(t('crm.arquivos.enviados'));
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const apagar = useMutation({
    mutationFn: (a: AnexoNegociacao) => del(`/anexos/${a.id}`),
    onSuccess: async () => {
      setExcluir(null);
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <section className="flex flex-col gap-3" data-aba-arquivos="">
      {podeEditar && (
        <button
          type="button"
          onClick={() => entrada.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setArrastando(true);
          }}
          onDragLeave={() => setArrastando(false)}
          onDrop={(e) => {
            e.preventDefault();
            setArrastando(false);
            const fs = [...e.dataTransfer.files];
            if (fs.length) enviar.mutate(fs);
          }}
          className={`flex flex-col items-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-sm ${arrastando ? 'border-acento-forte bg-sup-2' : 'border-borda'}`}
          data-soltar-arquivo=""
        >
          <Upload className="size-6 text-tinta-3" aria-hidden />
          {t('crm.arquivos.soltar')}
        </button>
      )}
      <input
        ref={entrada}
        type="file"
        multiple
        hidden
        data-arquivo-negociacao=""
        onChange={(e) => {
          const fs = [...(e.target.files ?? [])];
          e.target.value = '';
          if (fs.length) enviar.mutate(fs);
        }}
      />
      <Estado consulta={q}>
        {(d) =>
          d.anexos.length === 0 ? (
            <p className="text-center text-sm text-tinta-3">{t('crm.arquivos.vazio')}</p>
          ) : (
            // Lista de 30/10 (B), item 19: a mesma lista do seletor "Escolher dos arquivos da negociação" do e-mail.
            <ListaArquivosNegociacao
              arquivos={d.anexos}
              nome={arq}
              baixar
              acoes={(a) =>
                podeEditar && <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.arquivos.excluir', { nome: arq(a.nome) })} icone={<Trash2 className="size-4" />} onClick={() => setExcluir(a as AnexoNegociacao)} />
              }
            />
          )
        }
      </Estado>
      <Confirmar aberto={!!excluir} aoFechar={() => setExcluir(null)} perigoso carregando={apagar.isPending} titulo={t('crm.arquivos.confirmarExcluir')} texto={excluir ? arq(excluir.nome) : ''} aoConfirmar={() => excluir && apagar.mutate(excluir)} />
    </section>
  );
}
